import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { PMT2Product } from '@/types/pmt2Types';
import usePmt3SyncStatus from '@/hooks/usePmt3SyncStatus'; // Restore the hook import
import ImageModal from './ImageModal';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { pmt2Service } from '@/services/pmt2Service';

interface ProductHeaderProps {
  product: PMT2Product | null;
}

/**
 * Component to display product header information above the main card
 */
const ProductHeader: React.FC<ProductHeaderProps> = ({ product }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { syncStatus, loading: syncLoading, error: syncError } = usePmt3SyncStatus(); // Restore the hook usage
  const { selectedCompany } = usePMT3Selection();
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  
  useEffect(() => {
    const loadSpreadsheetConfig = async () => {
      if (!selectedCompany) {
        setSpreadsheetId(null);
        return;
      }
      try {
        setConfigLoading(true);
        const configs = await pmt2Service.getSpreadsheetConfigs();
        const config = configs.find(c => c.companyName.toLowerCase() === selectedCompany.toLowerCase());
        setSpreadsheetId(config ? config.spreadsheetId : null);
      } catch (error) {
        console.error('Error loading spreadsheet config:', error);
        setSpreadsheetId(null);
      } finally {
        setConfigLoading(false);
      }
    };

    loadSpreadsheetConfig();
  }, [selectedCompany]);
  
  if (!product) return null;

  // Get all image URLs from the product
  const getProductImages = (product: PMT2Product): string[] => {
    if (!product) return [];
    
    const images: string[] = [];
    
    // Check for numbered image URLs (Image URL 1, Image URL 2, etc.)
    for (let i = 1; i <= 8; i++) {
      const imageKey = `Image URL ${i}`;
      if (product[imageKey] && typeof product[imageKey] === 'string' && 
          product[imageKey] !== '--' && product[imageKey] !== '_' && 
          product[imageKey] !== '-' && !product[imageKey].startsWith('_')) {
        images.push(product[imageKey]);
      }
    }
    
    // If no numbered images found, check for the generic Image URL field
    if (images.length === 0 && product['Image URL'] && 
        typeof product['Image URL'] === 'string' && 
        product['Image URL'] !== '--' && product['Image URL'] !== '_' && 
        product['Image URL'] !== '-' && !product['Image URL'].startsWith('_')) {
      images.push(product['Image URL']);
    }
    
    return images;
  };

  const images = getProductImages(product);
  const productTitle = product['Product Name'] || product.Title || product.id;
  
  // Handle navigation in the modal
  const handlePrevious = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
  };
  
  const handleNext = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm mb-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        {/* Image moved to the left */}
        {images.length > 0 && (
          <div className="flex-shrink-0 mr-4">
            <img 
              src={images[0]} 
              alt={productTitle}
              className="h-16 w-16 object-contain cursor-pointer border border-gray-200 rounded"
              onClick={() => setIsModalOpen(true)}
              title="Click to enlarge"
            />
          </div>
        )}
        <div className="flex-grow">
          <h2 className="text-xl font-bold">{productTitle}</h2>
          <div className="mt-1 flex items-center flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">ASIN:</span>
            <a
              href={`https://www.amazon.com/dp/${product.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 flex items-center gap-1"
            >
              {product.id} <ExternalLink className="h-3 w-3" />
            </a>
            {spreadsheetId && selectedCompany && !configLoading && (
              <>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Sheets:</span>
                <a
                  href={`https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 dark:text-green-400 text-sm font-medium hover:underline flex items-center gap-1"
                  title={`Open ${selectedCompany} Spreadsheet`}
                >
                  {selectedCompany} <ExternalLink className="h-3 w-3" />
                </a>
              </>
            )}
          </div>
        </div>


        <div className="flex-shrink-0 text-right">
          {/* Change label back to Last Updated, but keep using the global sync time */}
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</div>
 
          <div>
            {syncLoading ? 'Loading...' : 
             syncError ? 'Error' : 
             syncStatus?.lastSync ? new Date(syncStatus.lastSync.toDate()).toLocaleString() : '-'}
          </div>
        </div>
      </div> {/* Close inner flex div */}

      {/* Image Modal for enlarged view */}
      <ImageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        images={images}
        currentIndex={currentImageIndex}
        onPrevious={handlePrevious}
        onNext={handleNext}
        alt={productTitle}
      />
    </div> // Close outer div
  );
};

export default ProductHeader;